"use client";

import { c as _c } from "react/compiler-runtime";
import type { Offer } from "@local/offer/src/types";
import { getT } from "@local/i18n/src/t";
import { getLanguageForMarket } from "@local/i18n/src/languages";
import { buy } from "@local/i18n/src/catalogs/buy";
import { pdp } from "@local/i18n/src/catalogs/pdp";
import { CreditCards } from "../../ui/CreditCards";
import { cn } from "@local/utils/src/cn";
import { getCdnUrl } from "@local/utils/src/getCdnUrl";
import Image from "next/image";
import { barlowCondensed } from "../../../utils/fonts";
import { Button } from "../../ui/Button";
import type { Route } from "next";
import { ContentBlock } from "../../ui/ContentBlock";
import { CallToAction } from "@local/offer/src/modules/common/ButtonBuy/CallToAction";
import { useSearchParams } from "next/navigation";
import { type ReactNode, Suspense, useMemo } from "react";
import { VideoPlayer } from "../../ui/VideoPlayer";
export interface PromoHeadlineVideoWithStarsProps {
  content: Offer.PromoHeadlineVideoWithStars;
  market?: string;
  product?: Offer.PinProduct;
  prices?: Offer.ListingPrices;
  trustpilotSlot?: ReactNode;
  /** Set for above-the-fold placements (e.g. first builder block) so the fallback image preloads as the LCP candidate. */
  priority?: boolean;
}
const VideoFallBack = t0 => {
  const $ = _c(15);
  const {
    content,
    market,
    priority
  } = t0;
  let t1;
  let t2;
  if ($[0] !== content.fallbackImage || $[1] !== market || $[2] !== priority) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const t = getT(getLanguageForMarket(market), {
        pdp
      });
      if (content.fallbackImage) {
        const t3 = content.fallbackImage.height || 1440;
        const t4 = content.fallbackImage.url as string;
        const t5 = content.fallbackImage.width || 1920;
        let t6;
        if ($[5] !== t4 || $[6] !== t5) {
          t6 = getCdnUrl({
            src: t4,
            width: t5
          });
          $[5] = t4;
          $[6] = t5;
          $[7] = t6;
        } else {
          t6 = $[7];
        }
        const t7 = content.fallbackImage.width || 1440;
        let t8;
        if ($[8] !== priority || $[9] !== t3 || $[10] !== t6 || $[11] !== t7) {
          t8 = <Image alt={"B\xC6RSkin Tactical Supply Co."} height={t3} src={t6} width={t7} sizes="100vw" priority={priority} className="absolute top-0 z-10 h-full w-full object-cover" />;
          $[8] = priority;
          $[9] = t3;
          $[10] = t6;
          $[11] = t7;
          $[12] = t8;
        } else {
          t8 = $[12];
        }
        t2 = t8;
        break bb0;
      }
      t1 = t("pdp.loadingPlayer");
    }
    $[0] = content.fallbackImage;
    $[1] = market;
    $[2] = priority;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[13] !== t1) {
    t3 = <div>{t1}</div>;
    $[13] = t1;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
export const VideoAndImage = t0 => {
  const $ = _c(11);
  const {
    content,
    market,
    priority
  } = t0;
  if (content.videoProvider !== "bunny") {
    return null;
  }
  const t1 = content.brightBackground ? "bg-white/50" : "bg-black/30";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("absolute inset-0 z-30 h-full w-full", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <div className={t2} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== content || $[5] !== market || $[6] !== priority) {
    t4 = content.videoId ? <div className="absolute inset-0 z-20 h-full w-full [&_video]:aspect-auto! [&_video]:h-full [&_video]:w-full [&_video]:object-cover [&>div]:h-full [&>div]:w-full"><VideoPlayer videoId={content.videoId} poster={content.fallbackImage?.url ? getCdnUrl({
        src: content.fallbackImage.url
      }) : ""} muted={true} loop={true} controls={false} autoplay={true} priority={!!priority} loadStrategy="idle" /></div> : <VideoFallBack content={content} market={market} priority={priority} />;
    $[4] = content;
    $[5] = market;
    $[6] = priority;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className="absolute inset-0 z-10 h-full w-full overflow-hidden">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
export const TextContent = t0 => {
  const $ = _c(39);
  const {
    content,
    market,
    product,
    prices,
    trustpilotSlot
  } = t0;
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams?.get("item_id");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const itemId = t1;
  let t2;
  bb0: {
    if (!product) {
      t2 = undefined;
      break bb0;
    }
    const originalPrice = prices?.[0]?.value ?? product.msrp;
    const rawDiscount = prices?.[0]?.discountValue;
    const defaultDiscountPrice = rawDiscount && rawDiscount > 0 ? rawDiscount : originalPrice;
    let matchedModifierPrice;
    let hasSpecificSkuMatch = false;
    if ($[2] !== itemId || $[3] !== prices || $[4] !== product.skuLists || $[5] !== product.skus || $[6] !== product.type) {
      const allSkus = product.type === "BUNDLE" ? product.skuLists.flatMap(_temp) : product.skus;
      const matchingSku = itemId ? allSkus.find(sku => sku.legacyId === itemId || sku.attributes.some(attr => attr.name === "payloadId" && attr.value === itemId) || sku.id === itemId || sku.name === itemId) : allSkus.find(_temp2);
      if (matchingSku) {
        hasSpecificSkuMatch = true;
        if (matchingSku.name && prices?.[0]?.modifiers?.length) {
          const matchedModifier = prices[0].modifiers.find(modifier => modifier.skus.includes(matchingSku.name as string));
          if (matchedModifier) {
            matchedModifierPrice = matchedModifier.amount;
          }
        }
      }
      $[2] = itemId;
      $[3] = prices;
      $[4] = product.skuLists;
      $[5] = product.skus;
      $[6] = product.type;
      $[7] = hasSpecificSkuMatch;
      $[8] = matchedModifierPrice;
    } else {
      hasSpecificSkuMatch = $[7];
      matchedModifierPrice = $[8];
    }
    let discountPrice;
    if (hasSpecificSkuMatch) {
      discountPrice = matchedModifierPrice ?? defaultDiscountPrice;
    } else {
      let t3;
      if ($[9] !== prices) {
        t3 = prices?.[0]?.modifiers?.length ? prices[0].modifiers.reduce(_temp3, prices[0].modifiers[0].amount) : undefined;
        $[9] = prices;
        $[10] = t3;
      } else {
        t3 = $[10];
      }
      const lowestModifierPrice = t3;
      discountPrice = lowestModifierPrice && lowestModifierPrice < defaultDiscountPrice ? lowestModifierPrice : defaultDiscountPrice;
    }
    let t3;
    if ($[11] !== discountPrice || $[12] !== originalPrice) {
      t3 = Math.round((originalPrice - discountPrice) / originalPrice * 100);
      $[11] = discountPrice;
      $[12] = originalPrice;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    t2 = t3;
  }
  const discount = t2;
  let t3;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="absolute bottom-0 z-30 h-32 w-full bg-linear-to-b from-transparent to-gray-950 sm:hidden" />;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const t4 = content.brightBackground ? "text-gray-900" : "text-white";
  let t5;
  if ($[15] !== t4) {
    t5 = cn("z-10 mx-auto flex w-full max-w-7xl flex-col items-center justify-center md:items-start md:justify-start md:px-16 lg:my-10", t4);
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="z-0 h-[260px] sm:hidden" />;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== content.headline) {
    t7 = content.headline && <h2 className={cn("leading-tighter z-40 mb-4 w-fit text-center text-6xl font-medium uppercase md:text-left", barlowCondensed.className)} dangerouslySetInnerHTML={{
      __html: content.headline
    }} />;
    $[18] = content.headline;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== content.subtitle) {
    t8 = <p className="leading-body z-40 mb-5 max-w-[424px] text-center text-base font-semibold md:text-left">{content.subtitle}</p>;
    $[20] = content.subtitle;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== content.ctaContent || $[23] !== content.ctaUrl || $[24] !== discount || $[25] !== market) {
    t9 = content.ctaContent && <div className="z-40"><Button as="link" href={`/${market}${content.ctaUrl}` as Route} prefetch={false} className="w-full max-w-[360px] bg-red-600 font-medium uppercase hover:bg-red-700"><CallToAction input={content.ctaContent} action={getT(getLanguageForMarket(market), {
          buy
        })("buy.shopNow")} market={market} discount={discount} /></Button></div>;
    $[22] = content.ctaContent;
    $[23] = content.ctaUrl;
    $[24] = discount;
    $[25] = market;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== content.showReviewStars || $[28] !== trustpilotSlot) {
    t10 = content.showReviewStars && trustpilotSlot && <div className="z-40 -mt-1">{trustpilotSlot}</div>;
    $[27] = content.showReviewStars;
    $[28] = trustpilotSlot;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== content.showCreditCards) {
    t11 = content.showCreditCards && <div className="mx-auto max-w-[500px] rounded-full bg-white px-2 py-1 md:mx-0"><CreditCards showPayPal={true} /></div>;
    $[30] = content.showCreditCards;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t5 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t12 = <>{t3}<div className="relative w-full bg-gray-950 px-6 py-10 lg:py-20"><div className={t5}>{t6}{t7}{t8}{t9}{t10}{t11}</div></div></>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t5;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};
export const PromoHeadlineVideoWithStars = t0 => {
  const $ = _c(13);
  const {
    content,
    market,
    product,
    prices,
    trustpilotSlot,
    priority
  } = t0;
  let t1;
  if ($[0] !== content || $[1] !== market || $[2] !== priority) {
    t1 = <VideoAndImage content={content} market={market} priority={priority} />;
    $[0] = content;
    $[1] = market;
    $[2] = priority;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== content || $[5] !== market || $[6] !== prices || $[7] !== product || $[8] !== trustpilotSlot) {
    t2 = <Suspense><TextContent content={content} market={market} product={product} prices={prices} trustpilotSlot={trustpilotSlot} /></Suspense>;
    $[4] = content;
    $[5] = market;
    $[6] = prices;
    $[7] = product;
    $[8] = trustpilotSlot;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t1 || $[11] !== t2) {
    t3 = <ContentBlock className="relative flex h-full justify-center overflow-hidden" touchEdges={true}>{t1}{t2}</ContentBlock>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
function _temp(skuList) {
  return skuList.skus;
}
function _temp2(sku_0) {
  return sku_0.isPreselected === true;
}
function _temp3(min, m) {
  return m.amount < min ? m.amount : min;
}